import classnames from 'classnames';
import React, { useState } from 'react';
import { Flipped, Flipper } from 'react-flip-toolkit';

import { ActionButtons, EditAccountBox, GroupedSortableDnd, useDndContext } from '..';
import { useAccounts } from '../../../../../../fetchHooks/useAccount';
import { useAccountsGroupsQuery, useUpdateGroupsAndProperties } from '../../../../../../fetchHooks/useAccountsGroups';
import { useRefreshAccountProperties } from '../../../../../../fetchHooks/useAccountsProperties';
import { useRefreshCreditCards } from '../../../../../../fetchHooks/useCards';
import useTranslation from '../../../../../../hooks/useTranslation';
import { AccountCardItem, GroupedAccountCardItem } from '../../../../../../models/account';
import { handleMove } from '../../utils';

import './AccountsListContent.scss';

interface EditableListContentProps {
    onCompleted: () => void;
}

export const AccountsListContent: React.FC<EditableListContentProps> = ({ onCompleted }) => {
    const translated = useTranslation();

    const {
        lastSaved: [lastSaved],
        grouped: [groupedAccounts, setGroupedAccounts],
    } = useDndContext();

    const [invalidAccount, setInvalidAccount] = useState<AccountCardItem>();

    const accountsQuery = useAccounts();
    const groupsQuery = useAccountsGroupsQuery();
    const refreshAccountProperties = useRefreshAccountProperties();
    const refreshCreditCards = useRefreshCreditCards();
    const updateGroupsAndAccountProperties = useUpdateGroupsAndProperties();

    const handleChange = (account: GroupedAccountCardItem, checked: boolean) => {
        setInvalidAccount(undefined);
        const flatted = groupedAccounts.reduce((acc, cur) => [...acc, ...cur.values], [] as GroupedAccountCardItem[]);
        const numberOfFavorites = flatted.filter((v) => v.favorite).length;

        if (numberOfFavorites < 2 && !checked) {
            setInvalidAccount(account);
            return;
        }

        setGroupedAccounts([
            ...groupedAccounts.map((item) => ({
                ...item,
                values: [
                    ...item.values.map((i) =>
                        i.accountNumber.value === account.accountNumber.value ? { ...i, favorite: checked } : i,
                    ),
                ],
            })),
        ]);
    };

    const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        updateGroupsAndAccountProperties.mutateAsync({ lastSaved, groupedAccounts }).then(() => {
            refreshCreditCards();
            accountsQuery.refetch();
            groupsQuery.refetch();
            refreshAccountProperties();
            onCompleted();
        });
    };

    const moveUp = handleMove(groupedAccounts, setGroupedAccounts, -1);
    const moveDown = handleMove(groupedAccounts, setGroupedAccounts, 1);

    return (
        <div className={classnames('accounts-list-content')}>
            <h2 className="accounts-list-content__header">{translated.editAccounts.header}</h2>
            <p className="accounts-list-content__info">{translated.editAccounts.info}</p>
            <form onSubmit={onSubmit}>
                <Flipper flipKey={JSON.stringify(groupedAccounts)}>
                    <GroupedSortableDnd
                        renderItem={(account) => (
                            <GroupedSortableDnd.Item id={account.accountNumber.value}>
                                <Flipped
                                    key={account.accountNumber.value}
                                    flipId={`edit-account-box-${account.accountNumber.value}`}
                                >
                                    <EditAccountBox
                                        account={account}
                                        checked={account.favorite ?? false}
                                        onChange={handleChange}
                                        moveUp={moveUp}
                                        moveDown={moveDown}
                                        focused={false}
                                        invalid={invalidAccount === account}
                                    />
                                </Flipped>
                            </GroupedSortableDnd.Item>
                        )}
                    />
                </Flipper>
                <ActionButtons isLoading={updateGroupsAndAccountProperties.isPending} />
            </form>
        </div>
    );
};
